import { FC, Fragment, ReactNode, useState } from 'react';
import { Dropdown, Menu } from 'antd';

import { useReportComponentDefinitions } from '@/hooks/useReportComponentDefinitions';
import { useReportComponents } from '@/hooks/useReportComponents';
import { DropdownMenuLabel, DropdownPanel, DropdownPanelMenu, LearnMoreFooter, ReportComponentIcon } from '@/shared';
import { useReportComponentStore } from '@/stores';
import { ReportComponent, ReportComponentType } from '@/types';

import SelectOptionContent from '../../../../components/SelectOptionContent';

interface ReportComponentSelectionDropdownProps {
    component: ReportComponent;
    children?: ReactNode;
}

const editableDataVisualizationTypes = new Set<ReportComponentType>([
    ReportComponentType.STATISTIC,
    ReportComponentType.MULTIPLE_STATISTICS,
]);

const ReportComponentSelectionDropdown: FC<ReportComponentSelectionDropdownProps> = ({
    component,
    children,
}): JSX.Element => {
    const [search, setSearch] = useState('');

    const { allReportComponents } = useReportComponents();

    const filteredReportComponents = allReportComponents
        .map((group) => ({
            ...group,
            options: group.options.filter(
                (option) =>
                    (group.key === 'charts' || editableDataVisualizationTypes.has(option.key)) &&
                    `${option.label} ${option.key}`.toLowerCase().includes(search.trim().toLowerCase()),
            ),
        }))
        .filter((group) => group.options.length > 0);

    const { REPORT_COMPONENT_DEFINITIONS } = useReportComponentDefinitions();
    const { replaceDraftConfiguration, updateDraftProperties } = useReportComponentStore();

    const handleUpdateReportComponentType = (rct: string) => {
        const type = rct as ReportComponentType;
        const definition = REPORT_COMPONENT_DEFINITIONS[type];

        replaceDraftConfiguration(component.id, definition.defaultConfig());
        updateDraftProperties(component.id, { type, embedURL: '' });
    };

    return (
        <Dropdown
            trigger={['click']}
            popupRender={() => (
                <DropdownPanel
                    footer={<LearnMoreFooter topic='Components' variant='dropdown' />}
                    maxHeight={16 * 24}
                    minWidth={16 * 18}
                    search={{
                        placeholder: 'Filter Components...',
                        setValue: setSearch,
                        value: search,
                    }}
                    style={{ marginTop: '0.75rem' }}
                >
                    {filteredReportComponents?.map((rcs) => {
                        return (
                            <Fragment key={rcs?.key}>
                                <DropdownMenuLabel icon={rcs?.icon}>{rcs?.label}</DropdownMenuLabel>
                                <DropdownPanelMenu selectable={false}>
                                    {rcs?.options?.map((rc) => {
                                        return (
                                            <Menu.Item
                                                key={rc?.key}
                                                onClick={() => handleUpdateReportComponentType(rc?.key)}
                                            >
                                                <SelectOptionContent
                                                    icon={<ReportComponentIcon size={16} type={rc.key} />}
                                                    title={rc?.label}
                                                    description={rc?.description}
                                                    selected={rc?.key === component?.type}
                                                />
                                            </Menu.Item>
                                        );
                                    })}
                                </DropdownPanelMenu>
                            </Fragment>
                        );
                    })}
                </DropdownPanel>
            )}
        >
            <span>{children}</span>
        </Dropdown>
    );
};

export default ReportComponentSelectionDropdown;
